import * as A from 'astronomy-engine';
import { signal } from '@preact/signals';
import type { Place } from '../astro/frame';
import { type SkyEvent, eventEnd } from '../astro/events';
import { HIJRI_MONTHS, type HijriTime, type Starts, fmtHijriShort, hijriTime, isDecided, isSoughtMonth, monthKey, nextMonth, prevMonth, saudiDate, sunsetOn } from '../astro/hijri';
import { hijriNow, saudiStarts } from './saudi';
import { EVIDENCE } from '../data/islamEvidence';
import { addDays, clockAt, dayFrom, wallTime } from '../lib/localtime';
import { loadJSON, saveJSON } from '../state';
import { directionWords } from '../ui/format';
import { IA, MOON_WORDS, TITLE_MAX, mendReminder } from '../lib/reminders';

// The sky calendar: a year of events for one place (home, or anywhere chosen), the "On now" line, and the
// reminders sent before the notable ones. Times are always the chosen place's own.

const DAY = 86400e3;

/** The event to open the calendar at (from the "On now" line or a notification), by id. */
export const calendarFocus = signal<string | null>(null);
/** The place to open the calendar for, when it is not home (a reminder for another place, Tonight while previewing). */
export const calendarPlace = signal<Place | null>(null);

export const tzOf = (p: Place) => p.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
const observer = (p: Place) => new A.Observer(p.lat, p.lon, p.height ?? 0);

// ---------- Working out the year ----------
// The year (a month at a time) and the weeks around now are worked out in the events worker: see skyevents.ts.

export { computeYear, eventsAround } from './skyevents';

// ---------- Saying when, in the place's own time ----------

const fmts = new Map<string, Intl.DateTimeFormat>();
function fmt(tz: string, kind: 'time' | 'date' | 'day' | 'month') {
  const k = `${tz}|${kind}`;
  let f = fmts.get(k);
  if (!f) {
    const o: Intl.DateTimeFormatOptions =
      kind === 'time' ? { hour: '2-digit', minute: '2-digit' }
      : kind === 'date' ? { weekday: 'short', day: 'numeric', month: 'short' }
      : kind === 'day' ? { day: 'numeric', month: 'short' }
      : { month: 'long', year: 'numeric' };
    fmts.set(k, (f = new Intl.DateTimeFormat('en-GB', { ...o, timeZone: tz })));
  }
  return f;
}
export const timeIn = (t: number, tz: string) => fmt(tz, 'time').format(t);
/** To the nearest minute (eclipse contacts: 13:23:57 is 13:24, not 13:23). Seconds would claim more than the
 *  prediction can: published models differ by several seconds. */
export const clockIn = (t: number, tz: string) => fmt(tz, 'time').format(t + 30e3);
export const dateIn = (t: number, tz: string) => fmt(tz, 'date').format(t);
export const monthIn = (t: number, tz: string) => fmt(tz, 'month').format(t);
const ymd = (t: number, tz: string) => clockAt(t, tz).date;

// Nights are named by the date of their evening: the night of 21–22 October is "2026-10-21". The cut-offs are
// read on the local clock and the dates counted on the calendar, so they hold on the night the clocks change.
/** The night we are in, or the one coming: before 06:00 it is still last night. */
const nightNow = (now: number, tz: string) => dayFrom(now, tz, 6);
/** The night a moment belongs to: 04:58 on the 22nd is in the night of the 21st (anything before noon is). */
const nightAt = (t: number, tz: string) => dayFrom(t, tz, 12);
function whichNight(night: string, now: number, tz: string): 'tonight' | 'tomorrow night' | null {
  const cur = nightNow(now, tz);
  return night === cur ? 'tonight' : night === addDays(cur, 1) ? 'tomorrow night' : null;
}

/** "21–22 Oct" for the night `t` falls in. */
export function nightOf(t: number, tz: string): string {
  const eve = nightAt(t, tz);
  const label = (d: string) => fmt('UTC', 'day').format(Date.parse(`${d}T12:00:00Z`)).split(' ');
  const [d1, m1] = label(eve), [d2, m2] = label(addDays(eve, 1));
  return m1 === m2 ? `${d1}–${d2} ${m2}` : `${d1} ${m1}–${d2} ${m2}`;
}
/** The visible part of an eclipse: from the first stage seen here to the last. */
function seenSpan(e: SkyEvent): [number, number] {
  const seen = e.phases!.filter((x) => x.up);
  const span = seen.length ? seen : e.phases!;
  return [span[0].t, span[span.length - 1].t];
}
/** An eclipse stage's time, to the nearest minute. */
export const phaseClock = (_label: string, t: number, tz: string) => clockIn(t, tz);

/** When to look, in a few words: "Night of 21–22 Oct, best around 04:45", "Mon 2 Aug, 12:02 to 14:45". */
export function whenLabel(e: SkyEvent, tz: string): string {
  if (e.kind === 'meteor') return `Night of ${nightOf(e.t, tz)}, best around ${timeIn(e.t, tz)}`;
  if (e.kind === 'opposition') return `Night of ${nightOf(e.t, tz)}, highest around ${timeIn(e.t, tz)}`;
  if (e.kind === 'eclipse' && e.phases?.length) {
    const [a, b] = seenSpan(e);
    return `${dateIn(a, tz)}, ${clockIn(a, tz)} to ${clockIn(b, tz)}`;
  }
  // The evening of the 29th: when to look, with no time that could be taken for a prediction.
  if (e.kind === 'crescent') return `${dateIn(e.t, tz)}, after sunset`;
  // A day of the Hijri year: the day itself.
  if (e.kind === 'day') return dateIn(e.t, tz);
  return `${dateIn(e.t, tz)}, ${timeIn(e.t, tz)}`;
}

/** A shower's activity dates are the IMO's whole days (held at noon UT), the same everywhere. */
export const activeDay = (t: number) => fmt('UTC', 'date').format(t);

// ---------- The eclipse prayer ----------

/** The Prophet's words on eclipses, from the hadith quoted on "The stars in Islam" (Astra's translation), and their
 *  Arabic, cut at the same place: his words begin there. */
export function eclipseHadith(): { text: string; arabic: string; ref: string } {
  const e = EVIDENCE['H-04'];
  const i = e.english.indexOf('“Indeed, the sun and the moon');
  const j = e.arabic.indexOf('«إِنَّ الشَّمْسَ');
  return { text: i >= 0 ? e.english.slice(i) : e.english, arabic: j >= 0 ? e.arabic.slice(j) : e.arabic, ref: e.ref };
}
/** Whether to show the eclipse prayer under an eclipse: one that can be seen here, and not the faint penumbral kind. */
export const prayerFor = (e: SkyEvent) => e.kind === 'eclipse' && e.visible === true && e.type !== 'penumbral';

// ---------- Reminders ----------

/** `lat` and `lon` (to about 10 km) say where the sky matters for the cloud check: the place it was set for. `asked`
 *  marks one set with "Remind me" as it goes to the server (which sends those whatever the weather, and tries again). */
/** `hijri`: a reminder dated by Saudi Arabia's calendar, which the server works out again before it goes, in `tz`, the
 *  time zone of the place it was set for. */
export interface EventReminder { at: number; until: number; title: string; body: string; tag: string; url: string; clear?: boolean; look?: number; lat?: number; lon?: number; asked?: boolean; hijri?: HijriTime; tz?: string }

/** The instant the local clock at `tz` shows `hour`:00 on the local date of `t`. */
export const atLocalHour = (t: number, tz: string, hour: number) => wallTime(ymd(t, tz), tz, hour);

/** A meteor shower worth going out for: about 15 an hour or more at its peak under perfect skies. */
const majorShower = (e: SkyEvent) => e.kind === 'meteor' && (e.zhr ?? 0) >= 15;

/** Events worth a notification without being asked: what people would be sorry to miss. (The crescent's alerts come
 *  from Saudi Arabia's own announcements instead: server/watch.ts.) */
export function alertable(e: SkyEvent): boolean {
  if (e.kind === 'meteor') return majorShower(e);
  if (e.kind === 'eclipse') return e.visible === true && e.weight >= 2;
  if (e.kind === 'conjunction' || e.kind === 'opposition') return e.weight >= 3;
  if (e.kind === 'moon') return e.supermoon === true;
  return false;
}

/** The few events to name first: the major ones within six weeks, then the soonest notable ones, in date order. */
export function comingUp(events: SkyEvent[], now: number, n = 4): SkyEvent[] {
  const notable = events.filter((e) => e.weight >= 2 && (e.t >= now - 3600e3 || ((e.phases || e.kind === 'day') && eventEnd(e) >= now)));
  const major = notable.filter((e) => (e.weight >= 3 || alertable(e)) && e.t - now < 45 * DAY).slice(0, n);
  const rest = notable.filter((e) => !major.includes(e)).slice(0, n - major.length);
  return [...major, ...rest].sort((a, b) => a.t - b.t);
}

/** At most `n` characters, cut at the end of a word, with "…" if anything was cut. */
export const clip = (s: string, n: number) => {
  if (s.length <= n) return s;
  const cut = s.slice(0, n - 1);
  // Back to the end of the last whole word, unless the cut falls on one (or one word fills most of it).
  const end = s[n - 1] === ' ' ? cut.length : cut.lastIndexOf(' ');
  return `${(end > n / 2 ? cut.slice(0, end) : cut).replace(/[\s,;:·–-]+$/, '')}…`;
};
/** The link a reminder opens: the calendar at the event, for the place it was worked out for. */
function eventUrl(e: SkyEvent, p: Place) {
  // The server keeps links of up to 200 characters: a long name (in Arabic, say) is shortened to fit.
  for (let n = 40; ; n -= 4) {
    const q = new URLSearchParams({ panel: 'calendar', ev: e.id, lat: p.lat.toFixed(2), lon: p.lon.toFixed(2), place: p.name.slice(0, Math.max(0, n)), tz: tzOf(p) });
    const url = `/?${q.toString()}`;
    if (url.length <= 200 || n <= 0) return url;
  }
}
/** Four characters for the place (to about 10 km), so the same event at two places makes two notifications. */
function placeTag(p: Place) {
  let h = 0;
  for (const c of `${p.lat.toFixed(1)},${p.lon.toFixed(1)}`) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return (h % 1679616).toString(36).padStart(4, '0');
}
/** A short fixed tag for each event, part and place (well under the server's 40 characters, so nothing is cut). */
const tagFor = (e: SkyEvent, part: string, p: Place) => `ev-${hash(e.id)}-${part}-${placeTag(p)}`;
function hash(s: string) {
  let h = 2166136261;
  for (const c of s) h = Math.imul(h ^ c.charCodeAt(0), 16777619) >>> 0;
  return h.toString(36);
}

/** The notifications for one event at a place: the afternoon of its night, or before an eclipse and an hour before it. */
export function remindersFor(e: SkyEvent, p: Place): EventReminder[] {
  const tz = tzOf(p);
  // No use once the event is well over; and never sooner than a few hours after it is sent.
  const over = (e.phases?.length ? e.phases[e.phases.length - 1].t : e.t) + 2 * 3600e3;
  // For the cloud check: when the sky matters (a Moon phase's own moment is often by day, so the middle of its night).
  const look = e.kind === 'moon' ? wallTime(addDays(nightAt(e.t, tz), 1), tz, 0) : e.t;
  const r = (at: number, title: string, body: string, part: string, clear: boolean, hijri?: HijriTime): EventReminder => ({
    at, until: Math.max(over, at + 3 * 3600e3), title: clip(title, TITLE_MAX), body: clip(body, 240), tag: tagFor(e, part, p), url: eventUrl(e, p),
    ...(hijri ? { hijri, tz } : {}),
    // And where: the place it was set for, which may be a trip's.
    ...(clear ? { clear, look, lat: Math.round(p.lat * 10) / 10, lon: Math.round(p.lon * 10) / 10 } : {}),
  });
  if (e.kind === 'eclipse' && e.phases?.length) {
    const seen = e.phases.filter((x) => x.up);
    const first = (seen[0] ?? e.phases[0]).t;
    const times = seen.map((x) => `${x.label.toLowerCase()} ${phaseClock(x.label, x.t, tz)}`).join(', ');
    const safety = e.icon !== 'sun' ? '' : e.type === 'total' ? ' Wear eclipse glasses except during totality itself.' : ' Never look at the Sun without eclipse glasses.';
    const title = e.title.toLowerCase();
    // Expected, as the scholars say an eclipse is only known when seen (item 23); the prayer is a tap away (item 21).
    const body = `Expected from ${p.name}: ${times}.${safety}${prayerFor(e) ? ' Tap to read about the eclipse prayer.' : ''}`;
    // A lunar eclipse in the small hours is "tonight", the evening before; any other is "tomorrow", the day before
    // (a solar one at dawn is in the morning, not the night).
    const small = e.icon === 'moon' && Number(timeIn(first, tz).slice(0, 2)) < 6;
    return [
      small ? r(wallTime(nightAt(first, tz), tz, 20), `Tonight, ${IA}: ${title}`, body, 'eve', false) : r(wallTime(addDays(ymd(first, tz), -1), tz, 18), `Tomorrow, ${IA}: ${title}`, body, 'eve', false),
      r(first - 3600e3, `In an hour, ${IA}: ${title}`, body, '1h', false),
    ];
  }
  // A day of the Hijri year: the afternoon before, an hour before sunset and by 16:00, while the day before is still
  // the day before by the Hijri date too (as the white days); of no use after that sunset, when the day itself begins.
  if (e.kind === 'day' && e.hijri) {
    const n = e.day === 'arafah' ? 9 : 10;
    const hijri = { y: e.hijri.year, m: e.hijri.month, d: n - 1, h: -1 };
    const eve = addDays(ymd(e.t, tz), -1);
    const sunset = sunsetOn(eve, { lat: p.lat, lon: p.lon, tz });
    return [{ ...r(Math.min(wallTime(eve, tz, 16), sunset - 3600e3), `Tomorrow, ${IA}: ${e.title.replace(/^The /, 'the ')}`, `${n} ${HIJRI_MONTHS[e.hijri.month - 1]} ${e.hijri.year}.`, 'eve', false, hijri), until: sunset }];
  }
  if (e.kind === 'crescent' && e.hijri) {
    const m = prevMonth(e.hijri);
    return [r(atLocalHour(e.t, tz, 15), `Tonight, ${IA}: look for the crescent of ${HIJRI_MONTHS[e.hijri.month - 1]}`, 'Look low in the west just after sunset.', 'eve', false, { y: m.year, m: m.month, d: 29, h: 15 })];
  }
  // Night events: the afternoon before the night they fall in.
  const night = nightAt(e.t, tz);
  const evening = wallTime(night, tz, 16);
  const hour = Number(timeIn(e.t, tz).slice(0, 2));
  const whenTxt = hour >= 18 || hour < 1 ? 'Tonight' : hour < 9 ? 'Before dawn' : 'Today';
  if (e.kind === 'meteor') {
    const rate = e.rate != null && e.rate >= 1.5 ? `, about ${Math.round(e.rate)} an hour from ${p.name} if clear` : '';
    const moon = /The Moon \(\d+% lit\) is up/.test(e.detail) ? ' The Moon is up then, so only the brighter ones show.' : '';
    // The night by its Hijri date too: the night of 21–22 October is the night of the 11th, say (its day began at sunset).
    const hijri = fmtHijriShort(hijriNow(e.t, p));
    return [r(evening, `${whenTxt}, ${IA}: the ${e.title}`, `Best around ${timeIn(e.t, tz)} on the night of ${nightOf(e.t, tz)} (${hijri})${rate}. Find a dark spot and give your eyes 20 minutes.${moon}`, 'eve', true)];
  }
  if (e.kind === 'moon') {
    // Each phase as it is: a New Moon gives a dark sky (nothing is said of seeing it: BUILD-SPEC item 19), a quarter Moon is half
    // lit and up for half the night, and only a Full Moon rises around sunset. Times are worked out for the place.
    const w = MOON_WORDS, half = w.half, craters = w.craters;
    if (e.moonQuarter === 0) return [r(evening, w.newTitle, w.newBody, 'eve', true)];
    if (e.moonQuarter === 1) {
      const set = moonRiseSet(p, -1, evening);
      return [r(evening, w.firstTitle, `${half}${set ? ` It is in the evening sky until it sets at ${timeIn(set.t, tz)}.` : ''}${craters}`, 'eve', true)];
    }
    if (e.moonQuarter === 3) {
      const rise = moonRiseSet(p, 1, evening);
      return [r(evening, w.lastTitle, `${half}${rise ? ` It rises at ${timeIn(rise.t, tz)} and stays up into the morning.` : ''}${craters}`, 'eve', true)];
    }
    if (e.moonQuarter === 2) {
      const rise = moonRiseSet(p, 1, wallTime(night, tz, 12));
      const set = rise && moonRiseSet(p, -1, rise.t);
      // A reminder that arrives after moonrise (in winter a Full Moon can rise before 4 pm) says it is already up.
      const up = rise && (rise.t <= evening ? ` It has been up in the ${directionWords(rise.az)} since ${timeIn(rise.t, tz)}` : ` It rises in the ${directionWords(rise.az)} at ${timeIn(rise.t, tz)}`);
      const when = up ? `${up}${set ? ` and sets at ${timeIn(set.t, tz)}` : ''}.` : '';
      // "Supermoon" only for one that is: a Full Moon unusually close.
      return [r(evening, e.supermoon ? w.superTitle : w.fullTitle, `${e.supermoon ? e.detail : w.fullBody}${when}`, 'eve', true)];
    }
    return [r(evening, `Tonight, ${IA}: ${e.title}`, e.detail, 'eve', true)];
  }
  return [r(evening, `${whenTxt}, ${IA}: ${e.title.replace(/^The /, 'the ')}`, `${e.detail} Best around ${timeIn(e.t, tz)}.`, 'eve', true)];
}

/**
 * The white days (BUILD-SPEC item 36), for those who chose it: on the 12th of each of the next months, by Saudi Arabia's
 * calendar, an hour before sunset here (by 16:00 at the latest), that the 13th, 14th and 15th come next. Never in
 * Dhū al-Ḥijjah, whose 13th is a day of al-Tashrīq, nor in Ramaḍān, whose every day is fasted.
 */
export function whiteDaysReminders(p: Place, now: number, starts: Starts = saudiStarts.peek()): EventReminder[] {
  const tz = tzOf(p);
  const out: EventReminder[] = [];
  let m = saudiDate(ymd(now, tz), starts);
  for (let i = 0; i < 3; i++, m = { ...nextMonth(m), day: 1 }) {
    if (m.month === 12 || m.month === 9) continue;
    const hijri = { y: m.year, m: m.month, d: 12, h: -1 };
    const w = { lat: p.lat, lon: p.lon, tz };
    const at = hijriTime(hijri, starts, w);
    if (at <= now) continue;
    // Of no use after that sunset, when the 13th begins.
    out.push({ at, until: sunsetOn(ymd(at, tz), w), title: `Tomorrow, ${IA}: the white days`, body: `The 13th, 14th and 15th of ${HIJRI_MONTHS[m.month - 1]}.`, tag: `white-${monthKey(m)}`, url: '/?panel=tonight', hijri });
  }
  return out;
}

/** How soon a reminder set at the last moment goes: a few minutes from then. */
const SOON = 5 * 60e3;

/**
 * What "Remind me" sets at `now`: the event's reminders still to come; or, once all of those have passed but the
 * event has not, one later reminder (`late`): an hour before its best time or start, or else in a few minutes.
 * Nothing once the event is over.
 */
export function remindersToSet(e: SkyEvent, p: Place, now = Date.now()): { list: EventReminder[]; late?: 'hour' | 'soon' } {
  const all = remindersFor(e, p);
  const list = all.filter((r) => r.at > now);
  if (list.length) return { list };
  const tz = tzOf(p);
  // A day of the Hijri year: none once the sunset that begins it has come here (its reminder says "tomorrow", and the
  // Hijri date has turned); the day before, after its afternoon reminder, one in a few minutes, until that sunset.
  if (e.kind === 'day') {
    const sunset = sunsetOn(addDays(ymd(e.t, tz), -1), { lat: p.lat, lon: p.lon, tz });
    if (now + SOON >= sunset || !all.length) return { list: [] };
    return { list: [{ ...all[0], at: now + SOON, until: sunset }], late: 'soon' };
  }
  // An eclipse runs from its first stage seen here to its last. A Moon phase's time is the moment of the phase, often
  // by day: its reminder is about its night, which lasts until 06:00, and it has no best time. Anything else is at
  // its best at its time, and over then.
  const eclipse = e.kind === 'eclipse' && !!e.phases?.length;
  const start = eclipse ? (e.phases!.find((x) => x.up) ?? e.phases![0]).t : e.t;
  const end = e.kind === 'moon' ? wallTime(addDays(nightAt(e.t, tz), 1), tz, 6) : eclipse ? e.phases![e.phases!.length - 1].t : e.t;
  const hour = e.kind !== 'moon' && start - 3600e3 > now + 60e3 ? start - 3600e3 : null;
  // Never after the end: a minute before it at the latest, and none with less than a minute to send it in.
  const at = hour ?? Math.min(now + SOON, end - 60e3);
  if (at < now + 60e3) return { list: [] };
  const last = all[all.length - 1];
  // "In an hour" no longer fits an eclipse that is that close.
  const title = eclipse ? clip(`${at < start ? `Soon, ${IA}` : 'Now'}: ${e.title.toLowerCase()}`, TITLE_MAX) : last.title;
  // An eclipse's reminder is no use once it is over, so it is not tried again after that.
  return { list: [{ ...last, at, until: eclipse ? end : Math.max(last.until, at + 3 * 3600e3), title }], late: hour ? 'hour' : 'soon' };
}

/** The Moon's next rising (`dir` 1) or setting (−1) at a place within a day of `from`: when, and its compass bearing. */
function moonRiseSet(p: Place, dir: 1 | -1, from: number): { t: number; az: number } | null {
  const obs = observer(p);
  const r = A.SearchRiseSet(A.Body.Moon, obs, dir, new Date(from), 1);
  if (!r) return null;
  const eq = A.Equator(A.Body.Moon, r, obs, true, true);
  return { t: r.date.getTime(), az: A.Horizon(r, obs, eq.ra, eq.dec, 'normal').azimuth };
}

/** Reminders someone asked for with "Remind me", kept on the phone until they are past. */
export const askedReminders = signal<EventReminder[]>(loadAsked());
/** The reminders kept on the phone, those saved by the first sky calendar put right (and saved back once). */
function loadAsked(): EventReminder[] {
  const kept = loadJSON<EventReminder[]>('eventReminders', []).filter((x) => x && typeof x.until === 'number' && x.until > Date.now());
  const mended = kept.map((x) => (typeof x.title === 'string' && typeof x.body === 'string' && typeof x.url === 'string' ? mendReminder(x) : x));
  if (mended.some((x, i) => x !== kept[i])) saveJSON('eventReminders', mended);
  return mended;
}
export function setAsked(list: EventReminder[]) {
  askedReminders.value = list.filter((x) => x.until > Date.now()).slice(-40);
  saveJSON('eventReminders', askedReminders.value);
}
export const isAsked = (e: SkyEvent, p: Place) => askedReminders.value.some((x) => x.url === eventUrl(e, p));
/** The reminders asked for one event at a place (none if it was not asked for). */
export const askedFor = (e: SkyEvent, p: Place) => askedReminders.value.filter((x) => x.url === eventUrl(e, p));
/** Take an event's reminders away, or set them: `list`, or what "Remind me" would set now. */
export function toggleAsked(e: SkyEvent, p: Place, list?: EventReminder[]): boolean {
  if (isAsked(e, p)) {
    setAsked(askedReminders.peek().filter((x) => x.url !== eventUrl(e, p)));
    return false;
  }
  setAsked([...askedReminders.peek(), ...(list ?? remindersToSet(e, p).list)]);
  return true;
}

// ---------- Adding to a calendar ----------

const stamp = (t: number) => new Date(t).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
const icsText = (s: string) => s.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n');

/** An .ics file for one event, with an alarm: the visible part of an eclipse, or the best hours of a night. */
export function eventIcs(e: SkyEvent, p: Place): string {
  // A day of the Hijri year goes in as the whole day.
  const date = e.kind === 'day' ? ymd(e.t, tzOf(p)).replace(/-/g, '') : null;
  const seen = e.phases?.filter((x) => x.up) ?? [];
  const start = seen.length ? seen[0].t : e.night ? e.t - 2 * 3600e3 : e.t - 30 * 60e3;
  const end = seen.length ? seen[seen.length - 1].t : e.night ? e.t + 1800e3 : e.t + 3600e3;
  return [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Ahillah//Sky calendar//EN', 'CALSCALE:GREGORIAN', 'BEGIN:VEVENT',
    `UID:${e.id}-${p.lat.toFixed(2)}-${p.lon.toFixed(2)}@ahillah`, `DTSTAMP:${stamp(Date.now())}`,
    ...(date ? [`DTSTART;VALUE=DATE:${date}`] : [`DTSTART:${stamp(start)}`, `DTEND:${stamp(Math.max(end, start + 15 * 60e3))}`]),
    `SUMMARY:${icsText(e.title)}`, `DESCRIPTION:${icsText(`${whenLabel(e, tzOf(p))}. ${e.detail}`)}`, `LOCATION:${icsText(p.name)}`,
    'BEGIN:VALARM', 'ACTION:DISPLAY', `DESCRIPTION:${icsText(e.title)}`, `TRIGGER:${e.kind === 'eclipse' ? '-PT1H' : '-PT3H'}`, 'END:VALARM',
    'END:VEVENT', 'END:VCALENDAR',
  ].join('\r\n');
}

export function downloadEventIcs(e: SkyEvent, p: Place) {
  const blob = new Blob([eventIcs(e, p)], { type: 'text/calendar' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${e.title.replace(/[^A-Za-z0-9]+/g, '-').replace(/^-|-$/g, '')}.ics`;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(a.href);
    a.remove();
  }, 1000);
}

// ---------- "On now" ----------

/** `key` names the event and what is said about it, so hiding "begin tonight" does not hide the peak. */
export interface OnNow { event: SkyEvent; text: string; key: string }

/**
 * The one thing worth a line on the sky right now, if any, in this order: an eclipse that can be seen here
 * (from three days before until it ends), the crescent of Ramaḍān, Shawwāl or Dhū al-Ḥijjah, a major shower's
 * peak or another major sight tonight or tomorrow night, a major shower's first night, then while it lasts.
 */
export function onNow(events: SkyEvent[], now: number, tz: string, dismissed: Set<string>, starts: Starts = saudiStarts.peek()): OnNow | null {
  const today = ymd(now, tz), cur = nightNow(now, tz);
  const say = (e: SkyEvent, stage: string, text: string): OnNow | null => (dismissed.has(`${e.id}:${stage}`) ? null : { event: e, text, key: `${e.id}:${stage}` });
  const first = (f: (e: SkyEvent) => OnNow | null) => {
    for (const e of events) {
      const r = f(e);
      if (r) return r;
    }
    return null;
  };
  return (
    first((e) => {
      if (e.kind !== 'eclipse' || !e.visible || e.weight < 2 || !e.phases) return null;
      const [a, b] = seenSpan(e);
      if (b < now || a - now > 3 * DAY) return null;
      if (a <= now) return say(e, 'now', `${e.title} now, until ${clockIn(b, tz)}${prayerFor(e) ? ' · the eclipse prayer' : ''}`);
      const h = Number(timeIn(a, tz).slice(0, 2));
      const d = ymd(a, tz);
      const word = e.icon === 'moon' && (h < 6 || h >= 18) ? (whichNight(nightAt(a, tz), now, tz) ?? dateIn(a, tz)) : d === today ? 'today' : d === addDays(today, 1) ? 'tomorrow' : dateIn(a, tz);
      return say(e, 'soon', `${e.title} ${word}, ${clockIn(a, tz)} to ${clockIn(b, tz)}`);
    }) ??
    first((e) => {
      // The evening of the 29th before Ramaḍān, Shawwāl or Dhū al-Ḥijjah: in the words of Saudi Arabia's call, until
      // Saudi Arabia has decided the month.
      if (e.kind !== 'crescent' || !e.hijri || !isSoughtMonth(e.hijri) || isDecided(e.hijri, starts) || e.t < now - 3 * 3600e3 || ymd(e.t, tz) !== today) return null;
      return say(e, 'soon', `Tonight Saudi Arabia looks for the crescent of ${HIJRI_MONTHS[e.hijri.month - 1]}`);
    }) ??
    first((e) => {
      if (!majorShower(e) || e.t < now - 3600e3) return null;
      const w = whichNight(nightAt(e.t, tz), now, tz);
      return w && say(e, 'peak', `${e.title} ${w}`);
    }) ??
    first((e) => {
      // ʿArafah and ʿĀshūrāʾ: the day before, and the day itself until its sunset (when the Hijri date turns).
      if (e.kind !== 'day') return null;
      const d = ymd(e.t, tz);
      return d === today && now < eventEnd(e) ? say(e, 'today', `${e.title} today`) : d === addDays(today, 1) ? say(e, 'soon', `${e.title} tomorrow`) : null;
    }) ??
    first((e) => {
      if (e.weight < 3 || e.kind === 'eclipse' || e.kind === 'crescent' || e.kind === 'meteor' || e.kind === 'day' || e.t < now - 3600e3) return null;
      const w = whichNight(nightAt(e.t, tz), now, tz);
      return w && say(e, 'soon', `${e.title}, ${w}`);
    }) ??
    first((e) => {
      if (!majorShower(e) || !e.active) return null;
      // The first night is the one before the first morning of activity; the last, the one before the last.
      const firstNight = addDays(new Date(e.active.from).toISOString().slice(0, 10), -1);
      const lastNight = addDays(new Date(e.active.to).toISOString().slice(0, 10), -1);
      const name = e.title.replace(/ peak$/, '');
      if (cur === firstNight) return say(e, 'begin', `${name} begin tonight, best night ${nightOf(e.t, tz)}`);
      if (cur < firstNight || cur > lastNight) return null;
      return e.t > now ? say(e, 'active', `${name} active, best night ${nightOf(e.t, tz)}`) : say(e, 'after', `${name} active until ${fmt('UTC', 'day').format(e.active.to)}`);
    })
  );
}

export const onNowDismissed = signal<Set<string>>(new Set(loadJSON<string[]>('onNowDismissed', [])));
export function dismissOnNow(key: string) {
  onNowDismissed.value = new Set([...onNowDismissed.peek(), key].slice(-60));
  saveJSON('onNowDismissed', [...onNowDismissed.value]);
}
